import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import {
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import ParentWrapper from "../../../components/ParentWrapper";
import Loader from "../../../components/AnimatedLoader";
import NoData from "../../../components/NoData";
import CommonFilter from "../../../components/CommonFilter";
import axiosInstance from "../../../services/axiosInterceptors";
import nipponBotApiEndpoints from "../../../services/nipponBotApiEndpoints";
import { theme as colorTheme } from "../../../Convoze.theme";

const primary = colorTheme("palette", "primary").value;
const primaryBg = colorTheme("palette", "primaryBg").value;
const positive = colorTheme("palette", "positive").value;
const negative = colorTheme("palette", "negative").value;
const neutral = colorTheme("palette", "neutral").value;
const border = "#eceff3";
const muted = "#6b7280";
const darkText = "#111827";
const chartPalette = [primary, "#10b981", "#f59e0b", "#ef4444", "#6366f1"];

const safeText = (value) => {
  const text = String(value || "").trim();
  return text && text.toLowerCase() !== "unknown" ? text : "-";
};

const formatPercent = (value) => `${Number(value || 0).toFixed(2).replace(/\.00$/, "")}%`;

const getCompletionRate = (completed, uploaded) => {
  const total = Number(uploaded || 0);
  if (!total) return "0%";
  return formatPercent((Number(completed || 0) / total) * 100);
};

const formatDuration = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.round((Number(milliseconds) || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

const getCandidateRowsFromListResponse = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const SectionTitle = ({ title, subtitle }) => (
  <Box>
    <Typography variant="h6" sx={{ fontWeight: 700, color: darkText }}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: muted }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const StatCard = ({ label, value, helper, onClick, tone = "default" }) => {
  const toneColor = tone === "success" ? positive : tone === "warning" ? neutral : tone === "error" ? negative : primary;
  return (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 1.25,
      borderRadius: 3,
      border: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      cursor: onClick ? "pointer" : "default",
      transition: "border-color 0.15s ease",
      "&:hover": onClick ? { borderColor: primary } : undefined,
    }}
  >
    <Typography variant="body2" sx={{ color: muted }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color: darkText }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: toneColor, fontWeight: 700, minHeight: "1.1em" }}>
      {helper || " "}
    </Typography>
  </Paper>
  );
};

const AlertCard = ({ label, value, description, onClick, tone = "warning" }) => {
  const toneColor = tone === "warning" ? "#a86a12" : primary;
  const toneBg = tone === "warning" ? "#fdf1dc" : primaryBg;
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 3,
        border: `1px solid ${toneBg}`,
        backgroundColor: toneBg,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ color: toneColor, fontWeight: 600, mb: 0.25 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: darkText }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: muted }}>
            {description}
          </Typography>
        </Box>
        {onClick ? <ArrowForwardIcon fontSize="small" sx={{ color: toneColor }} /> : null}
      </Box>
    </Paper>
  );
};

const MetricListCard = ({ title, items }) => (
  <Paper elevation={0} sx={{ p: 1.25, borderRadius: 3, border: `1px solid ${border}` }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25, color: darkText }}>
      {title}
    </Typography>
    <Grid container spacing={1.5} rowSpacing={1.25}>
      {items.map(([label, value, helper]) => (
        <Grid item xs={12} sm={6} key={label}>
          <Typography variant="body2" sx={{ color: muted }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: darkText }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: helper === "Needs review" ? "#a86a12" : muted, minHeight: "1.1em", display: "block" }}>
            {helper || " "}
          </Typography>
        </Grid>
      ))}
    </Grid>
  </Paper>
);

export default function NipponBotDashboard() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [payloadData, setPayloadData] = useState();
  const [notice, setNotice] = useState({ open: false, message: "", severity: "info" });
  const [opsAlerts, setOpsAlerts] = useState({ failedBatches: 0, pendingResumeCandidates: 0 });
  const [activeDateRange, setActiveDateRange] = useState({ startDate: "", endDate: "" });
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const navigateToCallsWithFilter = (status) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    const firstOfMonthStr = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];

    const filterState = {
      filters: {
        startDate: firstOfMonthStr,
        endDate: todayStr,
        sentiment: [],
        status: status,
        agents: [],
        branchManagers: [],
        campaigns: [],
        callInteraction: "",
        callCompliance: "",
        callDuration: [],
        disposition: [],
        subDisposition: [],
        callScore: [],
        conversionStatus: [],
        categoryDistribution: [],
      },
      appliedFilters: status ? [{ key: "status", label: `Status: ${status}` }] : [],
      frequentKeywords: "",
    };
    localStorage.setItem("nipponBotInteractionFilter", btoa(JSON.stringify(filterState)));
    navigate("/user/bot-interviews/calls");
  };

  useEffect(() => {
    // Clear any redirect status filters on mount
    const clearDashboardFilters = (storageKey) => {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(atob(stored));
          if (parsed?.filters?.status) {
            parsed.filters.status = "";
            parsed.appliedFilters = (parsed.appliedFilters || []).filter(f => f.key !== "status");
            localStorage.setItem(storageKey, btoa(JSON.stringify(parsed)));
          }
        } catch (e) {
          // ignore
        }
      }
    };
    clearDashboardFilters("nipponBotInteractionFilter");
    clearDashboardFilters("nipponBotInterviewQueueFilter");
  }, []);

  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    const firstOfMonthStr = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];

    const startDate = payloadData?.filters?.startDate || firstOfMonthStr;
    const endDate = payloadData?.filters?.endDate || todayStr;
    setActiveDateRange({ startDate, endDate });

    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(nipponBotApiEndpoints.compiledReport, {
          headers: { Authorization: `Bearer ${token}` },
          params: { startDate, endDate },
        });
        setReport(response?.data?.data || null);
      } catch (error) {
        setNotice({
          open: true,
          severity: "error",
          message: error?.response?.data?.status?.value || "Failed to load Nippon bot dashboard.",
        });
      } finally {
        setLastUpdatedAt(new Date());
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [payloadData, token]);

  useEffect(() => {
    const loadOpsAlerts = async () => {
      try {
        const [schedulesResponse, candidatesResponse] = await Promise.all([
          axiosInstance.get(nipponBotApiEndpoints.outboundSchedules, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axiosInstance.get(nipponBotApiEndpoints.candidateList, {
            headers: { Authorization: `Bearer ${token}` },
            params: { pagination: false },
          }),
        ]);
        const schedules = schedulesResponse?.data?.data || [];
        const candidatesList = getCandidateRowsFromListResponse(candidatesResponse);
        setOpsAlerts({
          failedBatches: schedules.filter((schedule) => (schedule?.summaryCounts?.failed || 0) > 0).length,
          pendingResumeCandidates: candidatesList.filter((candidate) => !candidate?.resume).length,
        });
      } catch (error) {
        // Non-critical widget - the rest of the dashboard should still work if this fails.
      }
    };

    loadOpsAlerts();
  }, [token]);

  const candidates = useMemo(() => report?.candidates || [], [report]);
  const recentCandidates = candidates.slice(0, 8);

  const operationStats = useMemo(
    () => [
      ["Uploaded Calls", report?.kpis?.totalUploadedCalls || 0, "", undefined],
      ["Completed Reports", report?.kpis?.completedReports || 0, getCompletionRate(report?.kpis?.completedReports, report?.kpis?.totalUploadedCalls), undefined, "success"],
      ["In Progress", report?.kpis?.processingCalls || 0, "", () => navigateToCallsWithFilter("In Progress"), "warning"],
      ["Failed", report?.kpis?.failedCalls || 0, "Needs attention", () => navigateToCallsWithFilter("Failed"), "error"],
    ],
    [report, navigate]
  );

  const candidateStats = useMemo(
    () => [
      ["Total Candidates", report?.kpis?.totalCandidates || 0, "", () => navigate("/user/bot-interviews/candidates")],
      ["Avg Qualification", formatPercent(report?.kpis?.averageQualificationScore || 0), "", undefined],
      ["Avg Call Duration", formatDuration(report?.kpis?.averageCallDuration || 0), "", undefined],
      ["Avg Completeness", formatPercent(report?.botPerformance?.averageCompleteness || 0), "", undefined],
    ],
    [report, navigate]
  );

  const bandChartData = useMemo(
    () => [
      {
        name: "Strong",
        value: candidates.filter((candidate) => candidate?.qualificationBand === "Strong").length,
      },
      {
        name: "Moderate",
        value: candidates.filter((candidate) => candidate?.qualificationBand === "Moderate").length,
      },
      {
        name: "Weak",
        value: candidates.filter((candidate) => candidate?.qualificationBand === "Weak").length,
      },
    ].filter((item) => item.value > 0),
    [candidates]
  );

  const scoreChartData = useMemo(
    () =>
      candidates.slice(0, 10).map((candidate, index) => ({
        name: safeText(candidate?.candidateName) === "-" ? `Candidate ${index + 1}` : candidate.candidateName,
        employer: safeText(candidate?.employer),
        qualification: Number(candidate?.qualificationScore) || 0,
        competency: Number(candidate?.competencyScore) || 0,
        m0: Number(candidate?.m0ReadinessScore) || 0,
      })),
    [candidates]
  );

  const totalBandCount = bandChartData.reduce((sum, item) => sum + item.value, 0);
  const completionRate = getCompletionRate(report?.kpis?.completedReports, report?.kpis?.totalUploadedCalls);
  const lastUpdatedLabel = lastUpdatedAt
    ? lastUpdatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    : "-";

  return (
    <ParentWrapper sx={{ minHeight: "calc(100vh - 45px)", gap: { xs: 1.5, md: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
        <CommonFilter
          setPayloadData={setPayloadData}
          storageName="nipponBotDashboard"
          isFilter={{ date: true, filter: false, callDuration: false, callScore: false }}
          defaultDateRange="month"
        />
        <Button
          variant="outlined"
          onClick={() => navigate("/user/bot-interviews/interviews")}
          endIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            alignSelf: { xs: "flex-start", md: "center" },
            color: primary,
            borderColor: primary,
            "&:hover": { borderColor: primary, backgroundColor: "rgba(118, 53, 221, 0.08)" },
          }}
        >
          Schedule Interviews
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          Showing {activeDateRange.startDate} to {activeDateRange.endDate}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          Last updated {lastUpdatedLabel}
        </Typography>
        {loading && report ? <CircularProgress size={12} /> : null}
      </Box>

      {!report && loading ? (
        <Loader />
      ) : (
        <>
      <SectionTitle title="Interview Operations" subtitle="Current interview-bot processing health" />
      <Grid container spacing={2}>
        {operationStats.map(([label, value, helper, onClick, tone]) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <StatCard label={label} value={value} helper={helper} onClick={onClick} tone={tone} />
          </Grid>
        ))}
      </Grid>

      <SectionTitle title="Needs Attention" subtitle="Operational items that need review" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <AlertCard
            label="Failed Batches"
            value={opsAlerts.failedBatches}
            description="Calls requiring retry"
            tone="warning"
            onClick={() => navigate("/user/bot-interviews/interviews")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AlertCard
            label="Candidates Pending Resume"
            value={opsAlerts.pendingResumeCandidates}
            description="Interviews cannot proceed"
            tone="accent"
            onClick={() => navigate("/user/bot-interviews/candidates")}
          />
        </Grid>
      </Grid>

      <SectionTitle title="Candidate Performance" subtitle="Candidate volume, qualification, and interview quality" />
      <Grid container spacing={2}>
        {candidateStats.map(([label, value, helper, onClick]) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <StatCard label={label} value={value} helper={helper} onClick={onClick} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <MetricListCard
            title="Extraction Performance"
            items={[
              ["Profile Fields", report?.botPerformance?.profileFieldsCollected || 0],
              ["Peer References", report?.botPerformance?.peerReferencesCollected || 0],
              ["Competency Topics", report?.botPerformance?.competencyTopicsAssessed || 0],
              ["M0 Questions", report?.botPerformance?.m0QuestionsAnswered || 0],
            ]}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <MetricListCard
            title="Data Quality"
            items={[
              ["Average Confidence", formatPercent(report?.botPerformance?.averageConfidence || 0), Number(report?.botPerformance?.averageConfidence || 0) < 50 ? "Needs review" : ""],
              ["Average Completeness", formatPercent(report?.botPerformance?.averageCompleteness || 0)],
              ["Missing / Unknown", report?.botPerformance?.missingOrUnknownCount || 0],
              ["Not Applicable", report?.botPerformance?.notApplicableCount || 0],
            ]}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: 1.25, borderRadius: 3, border: `1px solid ${border}`, overflow: "hidden" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25, color: darkText }}>
              Qualification Distribution
            </Typography>
            {!bandChartData.length ? (
              <NoData height="170px" />
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <Box sx={{ height: 170 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={bandChartData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={4}>
                          {bandChartData.map((entry, index) => (
                            <Cell key={entry.name} fill={chartPalette[index % chartPalette.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Stack spacing={1.25}>
                    {bandChartData.map((item, index) => (
                      <Box key={item.name}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.name}</Typography>
                          <Typography variant="body2" sx={{ color: muted }}>
                            {item.value} · {totalBandCount ? formatPercent((item.value / totalBandCount) * 100) : "0%"}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
                          <Box sx={{ width: totalBandCount ? `${(item.value / totalBandCount) * 100}%` : "0%", height: "100%", bgcolor: chartPalette[index % chartPalette.length] }} />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ p: 1.25, borderRadius: 3, border: `1px solid ${border}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25, color: darkText }}>
              Call Health
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 2 }}>
              {[
                ["Uploaded", report?.kpis?.totalUploadedCalls || 0],
                ["Completed", report?.kpis?.completedReports || 0],
                ["In Progress", report?.kpis?.processingCalls || 0],
                ["Failed", report?.kpis?.failedCalls || 0],
                ["Completion Rate", completionRate],
              ].map(([label, value]) => (
                <Box key={label} sx={{ minWidth: 96 }}>
                  <Typography variant="body2" sx={{ color: muted }}>{label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: label === "Failed" ? "#D03F18" : darkText }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, height: 10, bgcolor: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: completionRate, bgcolor: primary }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, overflow: "hidden" }}>
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${border}` }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: darkText }}>
            Top Candidate Performance
          </Typography>
          <Typography variant="body2" sx={{ color: muted }}>
            Top 10 candidates from the current report window
          </Typography>
        </Box>
        {!scoreChartData.length ? (
          <NoData height="250px" />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Candidate", "Employer", "Qualification", "Competency", "M0"].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scoreChartData.map((candidate) => (
                  <TableRow key={`${candidate.name}-${candidate.employer}`} hover>
                    <TableCell>{safeText(candidate.name)}</TableCell>
                    <TableCell>{candidate.employer}</TableCell>
                    <TableCell>{candidate.qualification}%</TableCell>
                    <TableCell>{candidate.competency}%</TableCell>
                    <TableCell>{candidate.m0}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #eceff3", overflow: "hidden" }}>
        <Box sx={{ p: 1.5, borderBottom: "1px solid #eceff3", display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Recent Candidate Reports
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon fontSize="small" />}
            onClick={() => navigate("/user/bot-interviews/compiled-report")}
            sx={{ color: primary, textTransform: "none", fontWeight: 700 }}
          >
            View All Reports
          </Button>
        </Box>
        {!recentCandidates.length ? (
          <NoData height="250px" />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Candidate", "Uploaded On", "Employer", "Competency", "M0", "Qualification", "Report"].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recentCandidates.map((candidate) => (
                  <TableRow key={candidate.transcriptionId || candidate.recordingId} hover>
                    <TableCell>{safeText(candidate.candidateName)}</TableCell>
                    <TableCell>{formatDateTime(candidate.uploadedDateTime)}</TableCell>
                    <TableCell>{safeText(candidate.employer)}</TableCell>
                    <TableCell>{candidate.competencyScore || 0}%</TableCell>
                    <TableCell>{candidate.m0ReadinessScore || 0}%</TableCell>
                    <TableCell>{candidate.qualificationScore || 0}%</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        disabled={!candidate.transcriptionId}
                        onClick={() => navigate(`/user/audio-analytics/${candidate.transcriptionId}`)}
                        sx={{
                          textTransform: "none",
                          color: primary,
                          borderColor: primary,
                          "&:hover": { borderColor: primary, backgroundColor: "rgba(118, 53, 221, 0.08)" },
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

        </>
      )}

      <Snackbar
        open={notice.open}
        autoHideDuration={5000}
        onClose={() => setNotice((current) => ({ ...current, open: false }))}
      >
        <Alert severity={notice.severity} onClose={() => setNotice((current) => ({ ...current, open: false }))}>
          {notice.message}
        </Alert>
      </Snackbar>
    </ParentWrapper>
  );
}
